import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ThrottleClickDirective } from '@directives/throttle-click.directive';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { formatISO9075 } from 'date-fns/formatISO9075';
import { AutosizeModule } from 'ngx-autosize';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MessageModule } from 'primeng/message';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { tap } from 'rxjs';

import { AudioClipComponent } from '../audio-clip/audio-clip.component';
import {
  AudioClipFormControl,
  AudioProjectFormService,
  AudioSegmentFormControl,
  createClipControl,
} from '../audio-project.form';
import { AudioProjectService } from '../audio-project.service';

interface AudioClipOption {
  name: string;
  index: number;
  control: FormGroup<AudioClipFormControl>;
}

@Component({
  selector: 'app-audio-segment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputGroupModule,
    InputGroupAddonModule,
    TextareaModule,
    AutosizeModule,
    CardModule,
    MessageModule,
    SelectModule,
    TooltipModule,
    ThrottleClickDirective,
    AudioClipComponent,
  ],
  templateUrl: './audio-segment.component.html',
  styleUrl: './audio-segment.component.scss',
})
export class AudioSegmentComponent implements OnInit {
  @Input() session: Session;
  @Input() idea: Idea;
  @Input() segment: FormGroup<AudioSegmentFormControl>;

  @Output() segmentAdd = new EventEmitter<number>();
  @Output() segmentDelete = new EventEmitter<number>();
  @Output() segmentReset = new EventEmitter<number>();

  hasChanges = false;
  selectedClip: AudioClipOption | undefined = undefined;

  get clips(): AudioClipOption[] {
    return this.segment.controls.clips.controls.map((control, index) => {
      return {
        name: formatISO9075(new Date(control.value.asset.createdAt)),
        index,
        control,
      };
    });
  }

  get selectedClipIndex(): number {
    if (this.segment.value.selectedClipIndex < 0) {
      return this.segment.controls.clips.length - 1;
    }
    return this.segment.value.selectedClipIndex;
  }

  set selectedClipIndex(selectedClipIndex: number) {
    this.segment.controls.selectedClipIndex.setValue(selectedClipIndex);
  }

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly audioProjectService: AudioProjectService,
    private readonly audioProjectFormService: AudioProjectFormService,
  ) {}

  ngOnInit(): void {
    this.audioProjectFormService
      .$onSegmentChanges(this.segment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([hasChanges]) => {
        this.hasChanges = hasChanges;
      });
    this.selectClip();
  }

  onAudioClipSelect({ value }: SelectChangeEvent) {
    this.selectedClipIndex = value.index;
  }

  onAudioClipGenerateClick() {
    this.audioProjectService
      .generateAudioSegment({
        sessionId: this.session._id,
        ideaId: this.idea._id,
        segmentId: this.segment.value.id,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(({ segment }) => {
          this.segment.controls.clips.push(
            new FormGroup(
              createClipControl(segment.clips.at(segment.selectedClipIndex)),
            ),
          );
          this.segment.controls.selectedClipIndex.setValue(
            segment.selectedClipIndex,
          );
          this.selectClip();
        }),
        tap(() => {
          this.audioProjectFormService.setSegmentInitialValue(this.segment);
        }),
      )
      .subscribe();
  }

  onAudioClipDeleteClick(clip: FormGroup<AudioClipFormControl>) {
    const index = this.findClipIndex(clip);
    if (index >= 0) {
      this.segment.controls.clips.removeAt(index);
      this.segment.controls.selectedClipIndex.setValue(
        Math.min(index, this.clips.length - 1),
      );
      this.selectClip();
    }
  }

  onDeleteClick() {
    this.segmentDelete.emit();
  }

  onResetClick() {
    this.segmentReset.emit();
  }

  onAddSectionClick() {
    this.segmentAdd.emit();
  }

  private selectClip() {
    this.selectedClip = this.clips.find(
      ({ index }) => index === this.selectedClipIndex,
    );
  }

  private findClipIndex(clip: FormGroup<AudioClipFormControl>) {
    return this.segment.controls.clips.controls.indexOf(clip);
  }
}
